import {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useMap} from '../hooks/useMap'
import {useSourceContext} from './Source'
import {deepEqual} from '../utils/deepEquals'

const updateLayerLayout = (map, props, prevProps) => {
  const layout = props.layout || {}
  const prevLayout = prevProps.layout || {}

  for (const key in layout) {
    if (!deepEqual(layout[key], prevLayout[key])) {
      map.setLayoutProperty(props.id, key, layout[key])
    }
  }

  for (const key in prevLayout) {
    if (!Object.hasOwn(layout, key)) {
      map.setLayoutProperty(props.id, key, undefined)
    }
  }
}

const updateLayerPaint = (map, props, prevProps) => {
  const paint = props.paint || {}
  const prevPaint = prevProps.paint || {}

  for (const key in paint) {
    if (!deepEqual(paint[key], prevPaint[key])) {
      map.setPaintProperty(props.id, key, paint[key])
    }
  }

  for (const key in prevPaint) {
    if (!Object.hasOwn(paint, key)) {
      map.setPaintProperty(props.id, key, undefined)
    }
  }
}

const updateLayerFilter = (map, props, prevProps) => {
  if (!deepEqual(props.filter, prevProps.filter)) {
    map.setFilter(props.id, props.filter)
  }
}

const updateLayerZoomRange = (map, props, prevProps) => {
  if (props.minzoom !== prevProps.minzoom || props.maxzoom !== prevProps.maxzoom) {
    map.setLayerZoomRange(props.id, props.minzoom, props.maxzoom)
  }
}

const updateLayerOrder = (map, props, prevProps) => {
  if (props.beforeId !== prevProps.beforeId) {
    map.moveLayer(props.id, props.beforeId)
  }
}

export const updateLayer = (map, props, prevProps) => {
  if (props.type === 'custom' || prevProps.type === 'custom') {
    return
  }

  updateLayerOrder(map, props, prevProps)
  updateLayerLayout(map, props, prevProps)
  updateLayerPaint(map, props, prevProps)
  updateLayerFilter(map, props, prevProps)
  updateLayerZoomRange(map, props, prevProps)
}

export const createLayer = (map, props, sourceId) => {
  const layerTypesWithSource = ['fill', 'line', 'symbol', 'raster', 'circle', 'fill-extrusion', 'heatmap', 'hillshade']

  if (layerTypesWithSource.includes(props.type)) {
    map?.addLayer(
      {
        ...props,
        source: props.source || sourceId
      },
      props.beforeId
    )
  } else if (props.type === 'background') {
    map?.addLayer(props, props.beforeId)
  }
}

/**
 * A component that adds a layer to a Maptiler
 *
 * @param {import('maplibre-gl').LayerSpecification} props - The component props
 * @returns {null} This component does not render any visible elements
 */
const Layer = ({...props}) => {
  const {map} = useMap()
  const {id: sourceId} = useSourceContext()
  const propsRef = useRef(props)

  useEffect(() => {
    return () => {
      if (map?.getLayer(props.id)) map?.removeLayer(props.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (map?.getLayer(props.id)) {
    updateLayer(map, props, propsRef.current)
  } else {
    createLayer(map, props, sourceId)
  }
  propsRef.current = props
  return null
}

Layer.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf([
    'fill',
    'line',
    'symbol',
    'raster',
    'circle',
    'fill-extrusion',
    'heatmap',
    'hillshade',
    'background',
    'custom'
  ]).isRequired,
  source: PropTypes.string,
  beforeId: PropTypes.string,
  layout: PropTypes.object,
  paint: PropTypes.object,
  filter: PropTypes.array,
  minzoom: PropTypes.number,
  maxzoom: PropTypes.number,
  metadata: PropTypes.object
}

export default Layer

/**
 * List of functions and their purpose:
 *
 * - updateLayerLayout: Updates the layout properties of a map layer if they have changed.
 * - updateLayerPaint: Updates the paint properties of a map layer if they have changed.
 * - updateLayerFilter: Updates the filter of a map layer if it has changed.
 * - updateLayerZoomRange: Updates the zoom range of a map layer if it has changed.
 * - updateLayerOrder: Updates the order of a map layer relative to other layers.
 * - updateLayer: Handles updating all properties of a map layer (layout, paint, filter, zoom range, and order).
 * - createLayer: Creates a new map layer and adds it to the map.
 * - Layer: React component that manages the lifecycle of a map layer in a Maptiler map.
 */
